import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

import { Events } from 'src/providers/events/events';
import { Communication } from 'src/providers/communication/communication';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { Loading } from 'src/shared/components/loading/loading';
import { CustomerGuestsApiService } from 'src/shared/services/customer-guests-api.service';
import { Util } from 'src/shared/util/util';
import validationUtils from 'src/shared/util/validation-utils';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { MessageThreadPage } from 'src/pages/messages/message-thread/message-thread';

import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';

import {
  CUSTOMER_GUESTS_CHANGED_EVENT,
  CustomerGuest,
  CustomerGuestCreatePayload,
  CustomerGuestUpdatePayload,
} from 'src/models/customer-guest.model';

type EditGuestMode = 'create' | 'edit';

interface GuestFormShape {
  name: FormControl<string>;
  email: FormControl<string>;
  phone: FormControl<string>;
}

const NON_BLANK = (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value ?? '').toString().trim();
  return value.length > 0 ? null : { required: true };
};

@Component({
  templateUrl: 'edit-guest.html',
  standalone: false,
})
export class EditGuestPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly events = inject(Events);
  private readonly modalCtrl = inject(ModalController);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly guestsApi = inject(CustomerGuestsApiService);
  private readonly communication = inject(Communication);
  private readonly util = inject(Util);
  private readonly windowService = inject(WindowService);

  mode: EditGuestMode = 'create';
  guest: CustomerGuest | null = null;
  // Set when opened from Messages on an unknown number: after creating the guest
  // we assign it to this chat room so the conversation shows their name.
  assignToChatRoomKey: string | null = null;
  assignType = 'guest';
  form: FormGroup<GuestFormShape>;
  submitted = false;
  loaded = false;
  isRightSideContent = true;
  errorMessage = '';
  dialogParams: any;
  canMessageGuest = false;
  phoneCountryCode = '1';

  boundSave = this.save.bind(this);
  boundMessageGuest = this.messageGuest.bind(this);

  constructor() {
    this.form = this.fb.nonNullable.group({
      name: this.fb.nonNullable.control('', [NON_BLANK, Validators.maxLength(100)]),
      email: this.fb.nonNullable.control('', [validationUtils.validateEmail]),
      phone: this.fb.nonNullable.control(''),
    });
  }

  @Loading('', true)
  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    this.mode = ((await this.getParam('mode')) as EditGuestMode) || 'create';
    this.guest = ((await this.getParam('guest')) as CustomerGuest | null) || null;
    // Read these directly (not via getParam) so they are never persisted to
    // storage and cannot leak into a later normal "Add Guest" from Contacts.
    this.assignToChatRoomKey = this.dialogParams?.assignToChatRoomKey ?? this.navCtrl.getParam('assignToChatRoomKey') ?? null;
    this.assignType = this.dialogParams?.assignType ?? this.navCtrl.getParam('assignType') ?? 'guest';
    this.rightSidePanelService.setDialogPageTitle(this.mode === 'edit' ? 'Edit Guest' : 'Add Guest');
    if (this.mode === 'edit') {
      // Opened from Messages with only an id: load the full guest record
      // authoritatively (the `guest` param may be a stale storage fallback).
      const guestId = this.dialogParams?.guestId ?? this.navCtrl.getParam('guestId');
      if (guestId !== null && guestId !== undefined) {
        try {
          this.guest = await firstValueFrom(this.guestsApi.getGuest(Number(guestId)));
        } catch (_err) {
          this.guest = null;
        }
      }
      if (this.guest) {
        this.form.patchValue({
          name: this.guest.name ?? '',
          email: this.guest.email ?? '',
          phone: this.guest.phone ?? '',
        });
        // A guest can be messaged over SMS once it has a phone, or immediately if a
        // conversation already exists (chat_room_key present).
        this.canMessageGuest = !!(this.guest.phone || this.guest.chat_room_key);
      }
    } else {
      const phone = this.dialogParams?.phone ?? this.navCtrl.getParam('phone');
      if (phone) {
        this.form.patchValue({ phone });
      }
    }
    this.loaded = true;
  }

  messageGuest(): void {
    if (!this.guest) {
      return;
    }
    const params: any = {
      backPage: 'messages',
      guestId: this.guest.id,
      pageTitle: this.guest.name || 'Guest',
      guestPhone: this.guest.phone,
    };
    if (this.guest.chat_room_key) {
      params.chatRoomKey = this.guest.chat_room_key;
    }
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('message-thread', params, MessageThreadPage);
    } else {
      this.navCtrl.navigateForward('message-thread', params);
    }
  }

  onCountryCodeChange(countryCode: string): void {
    this.phoneCountryCode = countryCode.replace('+', '');
  }

  async getParam(paramName: string): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName);
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    this.storage.save(paramName, param);
    return param;
  }

  @Loading('', true)
  async save() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.form.valid) {
      return;
    }

    try {
      if (this.mode === 'create') {
        const phone = this.normalizePhone(this.form.value.phone);
        const payload: CustomerGuestCreatePayload = {
          name: (this.form.value.name ?? '').trim(),
          ...(this.form.value.email ? { email: this.form.value.email.trim() } : {}),
          ...(phone ? { phone } : {}),
        };
        const createdGuest = await firstValueFrom(this.guestsApi.createGuest(payload));
        if (this.assignToChatRoomKey && createdGuest?.id !== null && createdGuest?.id !== undefined) {
          try {
            await this.communication.assignChat(this.assignToChatRoomKey, createdGuest.id, this.assignType);
          } catch (_err) {
            // Non-fatal: the guest was created; the number can be assigned later.
          }
        }
        this.util.showSuccess('Guest added');
      } else if (this.guest) {
        const payload: CustomerGuestUpdatePayload = this.buildUpdatePayload();
        if (Object.keys(payload).length === 0) {
          this.dismiss();
          return;
        }
        await firstValueFrom(this.guestsApi.updateGuest(this.guest.id, payload));
        this.util.showSuccess('Guest updated');
      }
      this.events.publish(CUSTOMER_GUESTS_CHANGED_EVENT);
      this.dismiss();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Could not save guest';
    }
  }

  async deleteGuest() {
    if (!this.guest) {
      return;
    }
    const params: ConfirmPageParamsModel = {
      title: 'Remove Guest',
      body: this.guest.name
        ? `Are you sure you want to remove ${this.guest.name}?`
        : 'Are you sure you want to remove this guest?',
      backText: 'Go Back',
      confirmText: 'Remove Guest',
      confirmAction: this.confirmDeleteGuest.bind(this),
      confirmButtonClass: 'secondary',
    };
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await modal.present();
  }

  private async confirmDeleteGuest() {
    if (!this.guest) {
      return;
    }
    try {
      await firstValueFrom(this.guestsApi.deleteGuest(this.guest.id));
      this.events.publish(CUSTOMER_GUESTS_CHANGED_EVENT);
      this.util.showSuccess('Guest removed');
      this.dismiss();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Could not remove guest';
      throw err;
    } finally {
      await this.modalCtrl.dismiss();
    }
  }

  private buildUpdatePayload(): CustomerGuestUpdatePayload {
    const payload: CustomerGuestUpdatePayload = {};
    const next = this.form.value;
    const nextName = (next.name ?? '').trim();
    const nextEmail = (next.email ?? '').trim();
    const nextPhone = this.normalizePhone(next.phone);
    if (nextName !== (this.guest?.name ?? '')) {
      payload.name = nextName;
    }
    if (nextEmail !== (this.guest?.email ?? '')) {
      payload.email = nextEmail;
    }
    if (nextPhone !== (this.guest?.phone ?? '')) {
      payload.phone = nextPhone;
    }
    return payload;
  }

  private normalizePhone(value: string | null | undefined): string {
    const phone = (value ?? '').trim();
    if (!phone) {
      return '';
    }
    const phoneWithCountryCode = phone.startsWith('+') ? phone.slice(1) : `${this.phoneCountryCode}${phone}`;
    return parsePhoneNumber(phoneWithCountryCode);
  }

  private dismiss() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
